// src/pages/emailMarketing/EmailMarketingDashboard.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { get } from '../../helpers/apiHelper';
import useAuthStore from '../../store/authStore';
import { FaEnvelope, FaPlus, FaUsers, FaChartLine } from 'react-icons/fa';
import emailMarketingService from '../../helpers/emailMarketingService';

function EmailMarketingDashboard() {
    const { user } = useAuthStore();
    const navigate = useNavigate();
    const [marcas, setMarcas] = useState([]);
    const [selectedMarca, setSelectedMarca] = useState(null);
    const [stats, setStats] = useState({
        totalCampaigns: 0,
        sentToday: 0,
        successRate: 0,
        activeContacts: 0
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchMarcas();
    }, [user]);

    useEffect(() => {
        if (selectedMarca) {
            fetchStats();
        }
    }, [selectedMarca]);

    const fetchMarcas = async () => {
        try {
            if (user?.userId) {
                const response = await get(`marcas/user/${user.userId}`);
                setMarcas(response);
                if (response.length > 0) {
                    setSelectedMarca(response[0]._id);
                }
            }
        } catch (error) {
            // Error silencioso
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            // Obtener campañas
            const campaigns = await emailMarketingService.getCampaigns(selectedMarca, {
                limit: 100
            });

            // Obtener contactos activos
            const contacts = await emailMarketingService.getContacts(selectedMarca, {
                acceptsMarketing: true,
                limit: 1
            });

            // Calcular estadísticas
            const totalCampaigns = campaigns.data?.length || campaigns.length || 0;
            const sentToday = campaigns.data?.filter(c => {
                if (!c.sentAt) return false;
                const sentDate = new Date(c.sentAt);
                const today = new Date();
                return sentDate.toDateString() === today.toDateString();
            }).length || 0;

            const totalSent = campaigns.data?.reduce((sum, c) => sum + (c.sentCount || 0), 0) || 0;
            const totalRecipients = campaigns.data?.reduce((sum, c) => sum + (c.totalRecipients || 0), 0) || 0;
            const successRate = totalRecipients > 0 ? ((totalSent / totalRecipients) * 100).toFixed(1) : 0;

            setStats({
                totalCampaigns,
                sentToday,
                successRate: parseFloat(successRate),
                activeContacts: contacts.pagination?.total || contacts.data?.length || 0
            });
        } catch (error) {
            // Error silencioso
        }
    };

    if (loading) {
        return (
            <div className="container mx-auto px-4 py-6">
                <div className="text-center">Cargando...</div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-6">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold">Email Marketing</h1>
                {marcas.length > 0 && (
                    <select
                        value={selectedMarca || ''}
                        onChange={(e) => setSelectedMarca(e.target.value)}
                        className="px-4 py-2 border border-gray-300 rounded"
                    >
                        {marcas.map(marca => (
                            <option key={marca._id} value={marca._id}>
                                {marca.nombre}
                            </option>
                        ))}
                    </select>
                )}
            </div>

            {/* Cards de métricas */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white p-6 rounded-lg shadow">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">Total Campañas</p>
                            <p className="text-2xl font-bold">{stats.totalCampaigns}</p>
                        </div>
                        <FaEnvelope className="text-blue-500 text-2xl" />
                    </div>
                </div>
                <div className="bg-white p-6 rounded-lg shadow">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">Enviados Hoy</p>
                            <p className="text-2xl font-bold">{stats.sentToday}</p>
                        </div>
                        <FaChartLine className="text-green-500 text-2xl" />
                    </div>
                </div>
                <div className="bg-white p-6 rounded-lg shadow">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">Tasa de Éxito</p>
                            <p className="text-2xl font-bold">{stats.successRate}%</p>
                        </div>
                        <FaChartLine className="text-purple-500 text-2xl" />
                    </div>
                </div>
                <div className="bg-white p-6 rounded-lg shadow">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">Contactos Activos</p>
                            <p className="text-2xl font-bold">{stats.activeContacts}</p>
                        </div>
                        <FaUsers className="text-orange-500 text-2xl" />
                    </div>
                </div>
            </div>

            {/* Acciones rápidas */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button
                    onClick={() => navigate('/email-marketing/campaigns/new')}
                    className="bg-blue-500 text-white px-6 py-4 rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center"
                >
                    <FaPlus className="mr-2" />
                    Nueva Campaña
                </button>
                <button
                    onClick={() => navigate('/email-marketing/contacts')}
                    className="bg-green-500 text-white px-6 py-4 rounded-lg hover:bg-green-600 transition-colors flex items-center justify-center"
                >
                    <FaUsers className="mr-2" />
                    Ver Contactos
                </button>
                <button
                    onClick={() => navigate('/email-marketing/campaigns')}
                    className="bg-purple-500 text-white px-6 py-4 rounded-lg hover:bg-purple-600 transition-colors flex items-center justify-center"
                >
                    <FaEnvelope className="mr-2" />
                    Ver Campañas
                </button>
            </div>
        </div>
    );
}

export default EmailMarketingDashboard;

